import { z } from 'zod';

/* ---------- Shared ---------- */

const DimensionSchema = z.object({
  unit: z.string().catch(''),
  value: z.number().catch(0),
});

const AddressSchema = z.object({
  address_line_one: z.string().catch(''),
  address_line_two: z.string().catch(''),
  city: z.string().catch(''),
  state: z.string().catch(''),
  zip_code: z.string().catch(''),
  country: z.string().catch(''),
});

/* ---------- Alias ---------- */

const AliasAttributesSchema = z.object({
  carton_length: DimensionSchema.catch({ unit: 'cm', value: 0 }),
  carton_width: DimensionSchema.catch({ unit: 'cm', value: 0 }),
  carton_height: DimensionSchema.catch({ unit: 'cm', value: 0 }),
  carton_net_weight: DimensionSchema.catch({ unit: 'kg', value: 0 }),
  carton_gross_weight: DimensionSchema.catch({ unit: 'kg', value: 0 }),
  units_per_carton: z.number().catch(0),
});

const AliasSchema = z.object({
  _id: z.string().catch(''),
  alias: z.string().catch(''),
  qty: z.number().catch(0),
  received_qty: z.number().catch(0),
  attributes: AliasAttributesSchema.nullable().catch(null),
  expiration_date: z.string().nullable().catch(null),

  is_inventory_deducted: z.boolean().catch(false),
  is_initial_amazon_connected_warehouse_qty_deducted: z.boolean().catch(false),
  is_deleted_shipment_inventory_deducted: z.boolean().catch(false),

  isVirtual: z.boolean().catch(false),
  itemLevel: z.boolean().catch(false),
});

/* ---------- Items ---------- */

const ItemImagesSchema = z.object({
  display_image: z.string().catch(''),
  all_images: z.array(z.string()).catch([]),
});

const ShipmentItemSchema = z.object({
  _id: z.string().catch(''),
  p_sku: z.string().catch(''),
  p_title: z.string().catch(''),
  upc: z.string().catch(''),
  qty: z.number().catch(0),
  supplierItemCost: z.number().catch(0),
  price: z.number().catch(0),
  p_images: ItemImagesSchema.catch({
    display_image: '',
    all_images: [],
  }),
  aliases: z.array(AliasSchema).catch([]),
});

/* ---------- Locations ---------- */

const OriginSchema = z.object({
  _id: z.string().catch(''),
  name: z.string().catch(''),
  currency: z.string().catch('USD'),
  address: AddressSchema.catch({
    address_line_one: '',
    address_line_two: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  }),
});

const DestinationSchema = z.object({
  _id: z.string().catch(''),
  name: z.string().catch(''),
  destination_type: z.string().catch(''),
  address: AddressSchema.nullable().catch(null),
});

/* ---------- Organization ---------- */

const OrganizationSchema = z.object({
  _id: z.string().catch(''),
  company_name: z.string().catch(''),
  logo: z
    .object({
      key: z.string().catch(''),
      bucket_name: z.string().catch(''),
      url: z.string().catch(''),
    })
    .catch({
      key: '',
      bucket_name: '',
      url: '',
    }),
});

/* ---------- Root ---------- */

const ShipmentTrackingSchema = z.object({
  _id: z.string().catch(''),
  shipment_id: z.string().nullable().catch(null),

  ETD: z.string().catch(''),
  ETA: z.string().catch(''),
  shipped: z.string().nullable().catch(null),
  arrived: z.string().nullable().catch(null),
  completed: z.string().nullable().catch(null),

  status: z.string().catch(''),
  progress: z.number().catch(0),

  qty_type: z.string().catch(''),
  qtyType: z.string().catch(''),

  display_id: z.string().catch(''),
  origin_type: z.string().catch(''),
  shipping_type: z.string().catch(''),

  carrier: z.string().catch('-'),

  origins: z.array(OriginSchema).catch([]),
  destination: DestinationSchema.catch({
    _id: '',
    name: '',
    destination_type: '',
    address: {
      address_line_one: '',
      address_line_two: '',
      city: '',
      state: '',
      zip_code: '',
      country: '',
    },
  }),

  items: z.array(ShipmentItemSchema).catch([]),
  components: z.array(z.any()).catch([]),
  origins_po: z.array(z.any()).catch([]),

  organization_id: z.string().catch(''),
  organization: OrganizationSchema.catch({
    _id: '',
    company_name: '',
    logo: { key: '', bucket_name: '', url: '' },
  }),

  seller_declared_value_items: z.number().catch(0),
  seller_declared_value_components: z.number().catch(0),
  seller_declared_value: z.number().catch(0),
  total_pallets: z.number().catch(0),
});

/* ---------- Type ---------- */

export type ShipmentListType = z.infer<typeof ShipmentTrackingSchema>;

export const ShipmentListResponseSchema = z.object({
  data: z.array(ShipmentTrackingSchema).catch([]),
  limit: z.number().catch(0),
  page: z.number().catch(0),
  totalCount: z.number().catch(0),
  totalPages: z.number().catch(0),
});
